import React, { useState } from 'react'
import { X, CheckCircle, XCircle, MessageSquare, ExternalLink } from 'lucide-react'

interface ReviewModalProps {
    isOpen: boolean
    onClose: () => void
    onReview: (decision: 'approved' | 'rejected', feedback?: string) => Promise<void>
    workSubmission: {
        deliveryUrls: string[]
        description: string
        submittedAt: Date
    }
    workerName: string
    gigTitle: string
    isLoading?: boolean
}

const ReviewModal: React.FC<ReviewModalProps> = ({
    isOpen,
    onClose,
    onReview,
    workSubmission,
    workerName,
    gigTitle,
    isLoading = false
}) => {
    const [decision, setDecision] = useState<'approved' | 'rejected' | null>(null)
    const [feedback, setFeedback] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSubmit = async () => {
        if (!decision || isSubmitting) return

        setIsSubmitting(true)
        try {
            await onReview(decision, feedback.trim() || undefined)
            // Reset form
            setDecision(null)
            setFeedback('')
            onClose()
        } catch (error) {
            console.error('Failed to submit review:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault()
            if (decision) {
                handleSubmit()
            }
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg max-w-2xl w-full mx-4 border border-gray-200 dark:border-gray-700 overflow-hidden max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Review Submitted Work
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {gigTitle} - Submitted by {workerName}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 rounded hover:opacity-80 transition-opacity text-gray-600 dark:text-gray-400"
                        aria-label="Close review modal"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Submission details */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900 dark:text-white">Work Submission</h4>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                Submitted {new Date(workSubmission.submittedAt).toLocaleDateString()}
                            </span>
                        </div>

                        {/* Delivery URLs */}
                        {workSubmission.deliveryUrls.length > 0 && (
                            <div className="mb-4">
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Delivery Links:</p>
                                <div className="space-y-2">
                                    {workSubmission.deliveryUrls.map((url, index) => (
                                        <a
                                            key={index}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors break-all"
                                        >
                                            <ExternalLink size={14} />
                                            {url}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Description */}
                        <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description:</p>
                            <div className="bg-white dark:bg-gray-600 rounded-md p-3">
                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                    {workSubmission.description}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Review decision */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Your Decision <span className="text-red-500">*</span>
                        </label>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setDecision('approved')}
                                className={`p-4 border-2 rounded-lg transition-all ${
                                    decision === 'approved'
                                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                                        : 'border-gray-200 dark:border-gray-600 hover:border-green-300 dark:hover:border-green-600 text-gray-700 dark:text-gray-300'
                                }`}
                                disabled={isSubmitting || isLoading}
                            >
                                <div className="flex items-center gap-3">
                                    <CheckCircle className={`w-5 h-5 ${decision === 'approved' ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`} />
                                    <div className="text-left">
                                        <p className="font-medium">Approve Work</p>
                                        <p className="text-xs opacity-75">Release payment to worker</p>
                                    </div>
                                </div>
                            </button>

                            <button
                                type="button"
                                onClick={() => setDecision('rejected')}
                                className={`p-4 border-2 rounded-lg transition-all ${
                                    decision === 'rejected'
                                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                                        : 'border-gray-200 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-600 text-gray-700 dark:text-gray-300'
                                }`}
                                disabled={isSubmitting || isLoading}
                            >
                                <div className="flex items-center gap-3">
                                    <XCircle className={`w-5 h-5 ${decision === 'rejected' ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`} />
                                    <div className="text-left">
                                        <p className="font-medium">Request Changes</p>
                                        <p className="text-xs opacity-75">Send back for revisions</p>
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Feedback */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Feedback {decision === 'rejected' && <span className="text-red-500">*</span>}
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                            {decision === 'approved'
                                ? 'Optional: Share what you liked about the work'
                                : decision === 'rejected'
                                ? 'Required: Explain what changes are needed'
                                : 'Share your thoughts about the submission'
                            }
                        </p>
                        <textarea
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={
                                decision === 'approved'
                                    ? "Great work! The design looks professional and meets all requirements..."
                                    : decision === 'rejected'
                                    ? "Please make the following changes: 1) Fix the responsive layout, 2) Add error handling..."
                                    : "Add your feedback here..."
                            }
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                            disabled={isSubmitting || isLoading}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Press Ctrl+Enter to submit quickly
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <button
                        onClick={onClose}
                        disabled={isSubmitting || isLoading}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!decision || (decision === 'rejected' && !feedback.trim()) || isSubmitting || isLoading}
                        className={`flex items-center gap-2 px-6 py-2 rounded-lg disabled:cursor-not-allowed transition-colors font-medium ${
                            decision === 'approved'
                                ? 'bg-green-600 hover:bg-green-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white'
                                : decision === 'rejected'
                                ? 'bg-red-600 hover:bg-red-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white'
                                : 'bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white'
                        }`}
                    >
                        {isSubmitting ? (
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <MessageSquare size={16} />
                        )}
                        {decision === 'approved' ? 'Approve & Release Payment' : decision === 'rejected' ? 'Request Changes' : 'Submit Review'}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ReviewModal